import Modal from "@material-ui/core/Modal";
import {
  EMPLOYEE_CATEGORY_OPTIONS,
  EMPLOYMENT_OPTIONS,
  OrgWorker,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
} from "model/OrganizationalStructure";
import { SalaryFormData } from "model/Salary";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";
import { ButtonComponent } from "../general/button/button.component";
import { DatePickerWithLabel } from "../general/datePickerWithLabel/datePickerWithLabel.component";
import { DropdownWithLabel } from "../general/dropDownWithLabel/dropdownWithLabel.component";
import { TextInputWithLabel } from "../general/textInput/textInput.component";
import { TopModal } from "../general/topModal/topModal.component";
import {
  formatDate,
  getFirstDayOfCurrentMonth,
} from "../organizationalStructure/createWorkerModal.component";
import { ModalBody, UpdateSalaryContainer } from "./salary.styled";
import { Api } from "services/api/api.service";
import { CustomIcon } from "components/icon/customIcon.component";
import { Color } from "config/colors";

type Props = {
  setShowUpdateWorkerSalaryModal: React.Dispatch<React.SetStateAction<boolean>>;
  showUpdateWorkerSalaryModal: boolean;
  selectedWorkerWithSalary: OrgWorker;
};

export const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

export const taxTableOptions = Array.from({ length: 36 - 29 + 1 }, (_, i) => {
  const value = 29 + i;
  return {
    value: String(value),
    label: String(value),
  };
});

const normalizeDateTimeNoFractionNoOffset = (value: string): string => {
  if (!value) return value;

  let v = value;

  v = v.replace(/(Z|[+-]\d{2}:\d{2})$/, "");
  v = v.replace(/\.\d+$/, "");

  return v;
};

export const UpdateWorkerSalaryModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [, monthSalaryActions] = useMonthSalaryStore();
  const [loading, setLoading] = useState(false);
  const hasEnded =
    !!props.selectedWorkerWithSalary.employmentInfo?.endDate &&
    Date.now() >
      new Date(props.selectedWorkerWithSalary.employmentInfo.endDate).getTime();

  const initialFormData: SalaryFormData = {
    employmentType: undefined,
    salaryType: undefined,
    salary: 0,
    startDate: normalizeDateTimeNoFractionNoOffset(getFirstDayOfCurrentMonth()),
    endDate: normalizeDateTimeNoFractionNoOffset(getFirstDayOfCurrentMonth()),
    workPercentage: 1,
    taxTable: 36,
    paymentType: "None",
    role: "-",
    employeeCategory: "",
    employeeNumber: 0,
  };

  const [formData, setFormData] = useState<SalaryFormData>(initialFormData);

  const isTraineeEmployee =
    (formData.employmentType ?? "").toLowerCase() === "trainee";

  const isConsultantEmployee =
    (formData.employmentType ?? "").toLowerCase() === "consultant";

  const isProjectEmployee =
    (formData.employmentType ?? "").toLowerCase() === "projectemployee";

  const employmentOptions = EMPLOYMENT_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const salaryOptions = SALARY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const employeeCategoryOptions = EMPLOYEE_CATEGORY_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  useEffect(() => {
    if (!props.selectedWorkerWithSalary) return;

    const current = props.selectedWorkerWithSalary.employmentInfo?.current;

    setFormData((prev) => ({
      ...prev,
      employmentType: current?.employmentType || undefined,
      salaryType: current?.salaryType || undefined,
      salary: current?.salary || 0,
      workPercentage: current?.workPercentage || 1,
      taxTable: current?.taxTable || 36,
      paymentType: current?.paymentType || "None",
      role: current?.role || "-",
      startDate: normalizeDateTimeNoFractionNoOffset(
        current?.startDate || prev.startDate,
      ),
      endDate: (current?.endDate ?? prev?.endDate)?.includes("9999-12-31")
        ? null
        : (current?.endDate ?? prev?.endDate)
          ? normalizeDateTimeNoFractionNoOffset(
              current?.endDate ?? prev?.endDate,
            )
          : null,
      employeeCategory:
        current?.employeeCategory || employeeCategoryOptions[0].value,
      employeeNumber: current?.employeeNumber || 0,
    }));
  }, [props.selectedWorkerWithSalary]);

  const [subscription, setSubscription] = useState<string | undefined>(
    undefined,
  );

  const getEmployeeNumber = () => {
    if (!companyState.company?.id) return;
    Api()
      .company.organizationalStructure.getEmployeeNumber(
        companyState.company?.id,
      )
      .then((res) => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: res.data,
        }));
      })
      .catch(() => {
        setFormData((prev) => ({
          ...prev,
          employeeNumber: 0,
        }));
      });
  };

  useEffect(() => {
    if (!companyState.company?.id) return;

    Api()
      .company.getCompanySubscriptionInfo(companyState.company.id)
      .then((res) => {
        setSubscription(res.data.membershipDisplayName);
      })
      .catch(() => {
        setSubscription(undefined);
      });
  }, [companyState.company?.id]);

  const isFlexCompany = useMemo(() => {
    if (subscription === undefined || subscription === null) return false;
    return subscription.includes("Flex");
  }, [subscription]);

  const hasSimflexityPay = useMemo(() => {
    const settings = companyState.company?.companySettings ?? [];
    return settings.some(
      (set) =>
        set.key === "SimflexityPay" && set.value?.toLowerCase() === "true",
    );
  }, [companyState.company?.companySettings]);

  const isSimflexityEmployee = formData.employmentType === "SimflexityEmployee";

  const salaryOptionsFiltered = useMemo(() => {
    if (!isSimflexityEmployee) return salaryOptions;
    return salaryOptions.filter((opt) => opt.value !== "Monthly");
  }, [isSimflexityEmployee, salaryOptions]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;

    setFormData((prev) => ({
      ...prev,
      salaryType: "Hourly",
    }));
  }, [isSimflexityEmployee, formData.salaryType]);

  const canSelectSimflexityPay = useMemo(() => {
    if (isSimflexityEmployee) return true;
    return (
      isFlexCompany && hasSimflexityPay && formData.salaryType === "Hourly"
    );
  }, [
    isSimflexityEmployee,
    isFlexCompany,
    hasSimflexityPay,
    formData.salaryType,
  ]);

  const paymentTypeOptions = useMemo(() => {
    const base = PAYMENT_TYPE_OPTIONS.map((option) => ({
      ...option,
      label: t(option.label),
    }));

    return base.filter((opt) => {
      if (opt.value === "SimflexityPay") return canSelectSimflexityPay;
      return true;
    });
  }, [t, canSelectSimflexityPay]);

  useEffect(() => {
    if (!isSimflexityEmployee) return;
    if (formData.paymentType === "SimflexityPay") return;

    setFormData((prev) => ({
      ...prev,
      paymentType: "SimflexityPay",
    }));
  }, [isSimflexityEmployee, formData.paymentType]);

  useEffect(() => {
    if (isSimflexityEmployee) return;
    if (formData.paymentType !== "SimflexityPay") return;
    if (canSelectSimflexityPay) return;

    setFormData((prev) => ({
      ...prev,
      paymentType: "None",
    }));
  }, [isSimflexityEmployee, formData.paymentType, canSelectSimflexityPay]);

  const handleUpdateSalary = () => {
    if (
      !companyState.company?.id ||
      !props.selectedWorkerWithSalary.workerId ||
      !formData.employmentType ||
      !formData.salaryType
    )
      return;

    setLoading(true);

    monthSalaryActions
      .updateWorkerEmployment(
        companyState.company.id,
        props.selectedWorkerWithSalary.workerId,
        normalizeDateTimeNoFractionNoOffset(formData.startDate),
        formData.employmentType,
        formData.salary,
        formData.salaryType,
        formData.workPercentage,
        formData.taxTable,
        formData.paymentType,
        formData.role,
        formData.endDate === null
          ? null
          : normalizeDateTimeNoFractionNoOffset(formData.endDate),
        formData.employeeCategory,
        formData.employeeNumber,
      )
      .then(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "checkmark",
            title: t("Salary.Changed"),
            message: t("Salary.ChangedWorkerEmployment"),
          },
        });
        props.setShowUpdateWorkerSalaryModal(false);
      })
      .catch((err: any) => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message:
              err.errorMessage == "Employee number is already in use"
                ? t("Alerts.EmployeeNumberIsAlreadyInUse")
                : err.errorMessage == "Selected start date is not allowed"
                  ? t("Alerts.DateNotAllowed")
                  : t("Alerts.SomethingWentWrong"),
          },
        });
      })
      .finally(() => setLoading(false));
  };

  const validateFields = () => {
    const basicFields = [
      formData.employmentType,
      formData.salaryType,
      formData.startDate,
      formData.role,
    ];

    if (basicFields.some((field) => !field)) return false;
    if (!isTraineeEmployee && formData.salary <= 0) return false;
    if ((isTraineeEmployee || isProjectEmployee) && formData.endDate === null)
      return false;
    if (formData.endDate !== null && formData.endDate < formData.startDate)
      return false;

    if (formData.salaryType === "Monthly") {
      if (!formData.workPercentage || formData.workPercentage <= 0.1)
        return false;
    }
    if (!isSimflexityEmployee && formData.paymentType === "SimflexityPay") {
      if (!canSelectSimflexityPay) return false;
    }

    return true;
  };

  useEffect(() => {
    if (isConsultantEmployee || isTraineeEmployee) {
      setFormData((prev) => ({
        ...prev,
        employeeCategory: "Undefined",
      }));
    }
  }, [formData.employmentType]);

  return (
    <>
      <Modal
        open={props.showUpdateWorkerSalaryModal}
        onClose={() => {
          props.setShowUpdateWorkerSalaryModal(false);
        }}
      >
        <ModalBody style={{ width: 600 }}>
          <TopModal
            setClose={() => props.setShowUpdateWorkerSalaryModal(false)}
            title={`${props.selectedWorkerWithSalary.firstName} ${
              props.selectedWorkerWithSalary.lastName
            }'s ${(() => {
              const s = t("OrganizationalStructure.Employment");
              return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
            })()}`}
          />

          <UpdateSalaryContainer>
            <TextInputWithLabel
              maxLength={24}
              label={t("OrganizationalStructure.Role")}
              required={true}
              value={formData.role}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  role: e.target.value,
                })
              }
              marginBottom={10}
              marginRight={10}
            />

            <DropdownWithLabel
              label={t("OrganizationalStructure.EmploymentType")}
              options={employmentOptions}
              value={formData.employmentType}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  employmentType: value,
                })
              }
              placeholder={t("OrganizationalStructure.ChooseEmploymentType")}
              required
            />
            {!isConsultantEmployee &&
              !isTraineeEmployee &&
              !isSimflexityEmployee && (
                <>
                  <DropdownWithLabel
                    label={t("OrganizationalStructure.EmployeeCategory")}
                    options={employeeCategoryOptions}
                    value={formData.employeeCategory}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        employeeCategory: value,
                      })
                    }
                    required
                  />

                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <TextInputWithLabel
                      maxLength={50}
                      label={t("OrganizationalStructure.EmployeeNumber")}
                      value={formData.employeeNumber?.toString()}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          employeeNumber: Number(e.target.value),
                        })
                      }
                      marginBottom={10}
                      marginRight={10}
                      required
                      width={formData.employeeNumber === 0 ? 180 : 250}
                    />
                    {formData.employeeNumber === 0 && (
                      <ButtonComponent
                        disabled={loading}
                        size={"small"}
                        onClick={() => getEmployeeNumber()}
                        action={true}
                        marginTop={10}
                        marginBottom={20}
                        marginRight={10}
                      >
                        <CustomIcon
                          name="reload"
                          color={Color.White}
                          padding="4px"
                          size="24px"
                        />
                      </ButtonComponent>
                    )}
                  </div>
                </>
              )}
            <DatePickerWithLabel
              label={t("OrganizationalStructure.StartDate")}
              required={true}
              value={formData.startDate ? new Date(formData.startDate) : null}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  startDate: normalizeDateTimeNoFractionNoOffset(
                    formatDate(date),
                  ),
                })
              }
              placeholder={t("OrganizationalStructure.ChooseDate")}
              dateFormat="YYYY-MM-DD"
              monthlySelectableDay={hasEnded ? undefined : 1}
            />
            <DatePickerWithLabel
              label={t("OrganizationalStructure.EndDate")}
              value={formData.endDate ? new Date(formData.endDate) : null}
              onChange={(date) =>
                setFormData({
                  ...formData,
                  endDate: date
                    ? normalizeDateTimeNoFractionNoOffset(formatDate(date))
                    : null,
                })
              }
              placeholder={t("OrganizationalStructure.ChooseDate")}
              dateFormat="YYYY-MM-DD"
              onFocus={() => {
                setFormData((prev) => ({
                  ...prev,
                  endDate: normalizeDateTimeNoFractionNoOffset(
                    formatDate(new Date()),
                  ),
                }));
              }}
              allowClear={!isProjectEmployee && !isTraineeEmployee}
              required={isProjectEmployee || isTraineeEmployee}
            />
            {!isTraineeEmployee && (
              <DropdownWithLabel
                label={t("OrganizationalStructure.SalaryModel")}
                options={salaryOptionsFiltered}
                value={formData.salaryType}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    salaryType: value,
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                required
                disabled={isSimflexityEmployee}
              />
            )}

            {formData.salaryType === "Monthly" &&
              !isTraineeEmployee &&
              !isSimflexityEmployee && (
                <TextInputWithLabel
                  maxLength={50}
                  label={`${t("OrganizationalStructure.WorkPercentage")} (%)`}
                  required={true}
                  value={
                    formData.workPercentage !== undefined &&
                    formData.workPercentage !== null
                      ? Math.round(formData.workPercentage * 100).toString()
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setFormData({
                        ...formData,
                        workPercentage: 0,
                      });
                      return;
                    }
                    if (!/^\d+$/.test(raw)) return;

                    const clampedPercent = clamp(parseInt(raw, 10), 0, 100);

                    setFormData({
                      ...formData,
                      workPercentage: clampedPercent / 100,
                    });
                  }}
                  type="number"
                  marginRight={10}
                  marginBottom={10}
                />
              )}

            {!isTraineeEmployee && (
              <TextInputWithLabel
                maxLength={7}
                label={
                  formData.salaryType === "Monthly"
                    ? t("OrganizationalStructure.Month")
                    : t("OrganizationalStructure.Hour")
                }
                value={formData.salary?.toString() || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    salary: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder={t("OrganizationalStructure.AddSalary")}
                type="number"
                required
                marginRight={10}
              />
            )}

            {!isTraineeEmployee && !isSimflexityEmployee && (
              <DropdownWithLabel
                label={t("OrganizationalStructure.TaxTable")}
                options={taxTableOptions}
                value={String(formData.taxTable)}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    taxTable: Number(value),
                  })
                }
                placeholder={t("OrganizationalStructure.ChooseSalaryModel")}
                required
              />
            )}

            {!isTraineeEmployee && (
              <DropdownWithLabel
                label={t("ProfileCompany.PaymentSolution")}
                options={paymentTypeOptions}
                value={formData.paymentType}
                disabled={isSimflexityEmployee}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    paymentType: value,
                  })
                }
                required
              />
            )}
          </UpdateSalaryContainer>

          <ButtonComponent
            disabled={!validateFields() || loading}
            size={"small"}
            onClick={handleUpdateSalary}
            action={true}
            marginTop={10}
            marginBottom={20}
            width={200}
            alignRight
          >
            {t("Salary.UpdateSalary")}
          </ButtonComponent>
        </ModalBody>
      </Modal>
    </>
  );
};
